/**
 * Data Prep Configuration Modals
 * 
 * Modal dialogs for configuring each type of data prep step.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    X, AlertCircle, Copy, Type, RefreshCw, TrendingUp, Calculator, Columns,
    Plus, Minus, Check, HelpCircle, ChevronDown
} from 'lucide-react';
import type {
    DataPrepStep,
    DataPrepStepType,
    NullHandlingStep,
    DuplicateHandlingStep,
    StringCleaningStep,
    DataTypeCastingStep,
    NormalizationStep,
    CalculatedColumnStep,
    ColumnManagementStep,
    ColumnSchema,
    NullReplacement,
    NormalizationMethod,
    ColumnManagementAction
} from '../../services/dataPrepTypes';
import { validateFormula, ALLOWED_FUNCTIONS } from '../../services/expressionParser';

// ============================================================================
// Shared Components
// ============================================================================

interface ModalWrapperProps {
    title: string;
    icon: React.FC<{ className?: string }>;
    onClose: () => void;
    onSave: () => void;
    children: React.ReactNode;
    isValid?: boolean;
}

const ModalWrapper: React.FC<ModalWrapperProps> = ({
    title,
    icon: Icon,
    onClose,
    onSave,
    children,
    isValid = true
}) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-gray-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-neon-gold/10 rounded-lg">
                        <Icon className="w-5 h-5 text-neon-gold" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-200">{title}</h3>
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-white/5 rounded-lg transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Content */}
            <div className="p-5 max-h-[60vh] overflow-y-auto">
                {children}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-white/10 bg-white/[0.02]">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 hover:bg-white/5 rounded-lg transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={onSave}
                    disabled={!isValid}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isValid
                        ? 'bg-neon-gold text-gray-900 hover:bg-neon-gold/90'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        }`}
                >
                    Save Step
                </button>
            </div>
        </div>
    </div>
);

interface SelectFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
    placeholder?: string;
}

const SelectField: React.FC<SelectFieldProps> = ({ label, value, onChange, options, placeholder }) => (
    <div className="space-y-1.5">
        <label className="block text-sm text-gray-400">{label}</label>
        <div className="relative">
            <select
                value={value}
                onChange={e => onChange(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-white/10 rounded-lg text-gray-200 appearance-none focus:outline-none focus:border-neon-gold/50"
                style={{ colorScheme: 'dark' }}
            >
                {placeholder && <option value="" className="bg-gray-800 text-gray-400">{placeholder}</option>}
                {options.map(opt => (
                    <option key={opt.value} value={opt.value} className="bg-gray-800 text-gray-200">{opt.label}</option>
                ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        </div>
    </div>
);

interface MultiSelectFieldProps {
    label: string;
    selected: string[];
    onChange: (selected: string[]) => void;
    options: string[];
    placeholder?: string;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({ label, selected, onChange, options, placeholder }) => (
    <div className="space-y-1.5">
        <label className="block text-sm text-gray-400">{label}</label>
        <div className="space-y-1 max-h-40 overflow-y-auto p-2 bg-white/5 border border-white/10 rounded-lg">
            {options.map(opt => (
                <label
                    key={opt}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded cursor-pointer"
                >
                    <input
                        type="checkbox"
                        checked={selected.includes(opt)}
                        onChange={e => {
                            if (e.target.checked) {
                                onChange([...selected, opt]);
                            } else {
                                onChange(selected.filter(s => s !== opt));
                            }
                        }}
                        className="rounded border-white/20 bg-white/5 text-neon-gold focus:ring-neon-gold/50"
                    />
                    <span className="text-sm text-gray-300">{opt}</span>
                </label>
            ))}
        </div>
    </div>
);

interface TextFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    error?: string;
    helpText?: string;
}

const TextField: React.FC<TextFieldProps> = ({ label, value, onChange, placeholder, error, helpText }) => (
    <div className="space-y-1.5">
        <label className="block text-sm text-gray-400">{label}</label>
        <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full px-3 py-2 bg-white/5 border rounded-lg text-gray-200 focus:outline-none ${error ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-neon-gold/50'
                }`}
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        {helpText && !error && <p className="text-xs text-gray-500">{helpText}</p>}
    </div>
);

interface CheckboxFieldProps {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    description?: string;
}

const CheckboxField: React.FC<CheckboxFieldProps> = ({ label, checked, onChange, description }) => (
    <label className="flex items-start gap-3 cursor-pointer">
        <input
            type="checkbox"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            className="mt-1 rounded border-white/20 bg-white/5 text-neon-gold focus:ring-neon-gold/50"
        />
        <div>
            <span className="text-sm text-gray-300">{label}</span>
            {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
    </label>
);

// ============================================================================
// Step-Specific Modals
// ============================================================================

interface NullHandlingModalProps {
    columns: ColumnSchema[];
    initialStep?: NullHandlingStep;
    onSave: (step: NullHandlingStep) => void;
    onClose: () => void;
}

export const NullHandlingModal: React.FC<NullHandlingModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [column, setColumn] = useState(initialStep?.column || '');
    const [action, setAction] = useState<'remove-rows' | 'replace'>(initialStep?.action || 'replace');
    const [replacement, setReplacement] = useState<NullReplacement>(initialStep?.replacement || 'mean');
    const [customValue, setCustomValue] = useState(String(initialStep?.customValue ?? ''));

    const isValid = column !== '';
    const columnOptions = columns.map(c => ({ value: c.name, label: `${c.name} (${c.stats.nullCount} nulls)` }));

    const handleSave = () => {
        onSave({
            type: 'null-handling',
            column,
            action,
            replacement: action === 'replace' ? replacement : undefined,
            customValue: replacement === 'custom' ? customValue : undefined
        });
    };

    return (
        <ModalWrapper title="Null Handling" icon={AlertCircle} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <SelectField
                    label="Column"
                    value={column}
                    onChange={setColumn}
                    options={columnOptions}
                    placeholder="Select column"
                />

                <SelectField
                    label="Action"
                    value={action}
                    onChange={v => setAction(v as 'remove-rows' | 'replace')}
                    options={[
                        { value: 'remove-rows', label: 'Remove rows with null values' },
                        { value: 'replace', label: 'Replace null values' }
                    ]}
                />

                {action === 'replace' && (
                    <>
                        <SelectField
                            label="Replace with"
                            value={replacement}
                            onChange={v => setReplacement(v as NullReplacement)}
                            options={[
                                { value: '0', label: 'Zero (0)' },
                                { value: 'mean', label: 'Mean (average)' },
                                { value: 'median', label: 'Median' },
                                { value: 'mode', label: 'Mode (most common)' },
                                { value: 'custom', label: 'Custom value' }
                            ]}
                        />

                        {replacement === 'custom' && (
                            <TextField
                                label="Custom value"
                                value={customValue}
                                onChange={setCustomValue}
                                placeholder="Enter value"
                            />
                        )}
                    </>
                )}
            </div>
        </ModalWrapper>
    );
};

interface DuplicateHandlingModalProps {
    columns: ColumnSchema[];
    initialStep?: DuplicateHandlingStep;
    onSave: (step: DuplicateHandlingStep) => void;
    onClose: () => void;
}

export const DuplicateHandlingModal: React.FC<DuplicateHandlingModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [keyColumns, setKeyColumns] = useState<string[]>(initialStep?.keyColumns || []);
    const columnNames = columns.map(c => c.name);

    const handleSave = () => {
        onSave({
            type: 'duplicate-handling',
            keyColumns
        });
    };

    return (
        <ModalWrapper title="Remove Duplicates" icon={Copy} onClose={onClose} onSave={handleSave} isValid={true}>
            <div className="space-y-4">
                <MultiSelectField
                    label="Key columns (leave empty for all columns)"
                    selected={keyColumns}
                    onChange={setKeyColumns}
                    options={columnNames}
                />
                <p className="text-xs text-gray-500">
                    Rows with identical values in the selected columns will be deduplicated. Only the first occurrence is kept.
                </p>
            </div>
        </ModalWrapper>
    );
};

interface StringCleaningModalProps {
    columns: ColumnSchema[];
    initialStep?: StringCleaningStep;
    onSave: (step: StringCleaningStep) => void;
    onClose: () => void;
}

export const StringCleaningModal: React.FC<StringCleaningModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [column, setColumn] = useState(initialStep?.column || '');
    const [operations, setOperations] = useState<('trim' | 'lowercase' | 'uppercase')[]>(initialStep?.operations || []);
    const [findValue, setFindValue] = useState(initialStep?.findReplace?.find || '');
    const [replaceValue, setReplaceValue] = useState(initialStep?.findReplace?.replace || '');

    const stringColumns = columns.filter(c => c.type === 'string');
    const columnOptions = stringColumns.map(c => ({ value: c.name, label: c.name }));
    const isValid = column !== '' && (operations.length > 0 || findValue !== '');

    const toggleOperation = (op: 'trim' | 'lowercase' | 'uppercase') => {
        if (operations.includes(op)) {
            setOperations(operations.filter(o => o !== op));
        } else {
            // lowercase and uppercase are mutually exclusive
            if (op === 'lowercase') {
                setOperations([...operations.filter(o => o !== 'uppercase'), op]);
            } else if (op === 'uppercase') {
                setOperations([...operations.filter(o => o !== 'lowercase'), op]);
            } else {
                setOperations([...operations, op]);
            }
        }
    };

    const handleSave = () => {
        onSave({
            type: 'string-cleaning',
            column,
            operations,
            findReplace: findValue ? { find: findValue, replace: replaceValue } : undefined
        });
    };

    return (
        <ModalWrapper title="String Cleaning" icon={Type} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <SelectField
                    label="Column"
                    value={column}
                    onChange={setColumn}
                    options={columnOptions}
                    placeholder="Select text column"
                />

                <div className="space-y-1.5">
                    <label className="block text-sm text-gray-400">Operations</label>
                    <div className="space-y-2">
                        <CheckboxField
                            label="Trim whitespace"
                            checked={operations.includes('trim')}
                            onChange={() => toggleOperation('trim')}
                            description="Remove leading and trailing spaces"
                        />
                        <CheckboxField
                            label="Convert to lowercase"
                            checked={operations.includes('lowercase')}
                            onChange={() => toggleOperation('lowercase')}
                        />
                        <CheckboxField
                            label="Convert to UPPERCASE"
                            checked={operations.includes('uppercase')}
                            onChange={() => toggleOperation('uppercase')}
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="block text-sm text-gray-400">Find & Replace (optional)</label>
                    <div className="grid grid-cols-2 gap-2">
                        <TextField
                            label=""
                            value={findValue}
                            onChange={setFindValue}
                            placeholder="Find..."
                        />
                        <TextField
                            label=""
                            value={replaceValue}
                            onChange={setReplaceValue}
                            placeholder="Replace with..."
                        />
                    </div>
                </div>
            </div>
        </ModalWrapper>
    );
};

interface TypeCastingModalProps {
    columns: ColumnSchema[];
    initialStep?: DataTypeCastingStep;
    onSave: (step: DataTypeCastingStep) => void;
    onClose: () => void;
}

export const TypeCastingModal: React.FC<TypeCastingModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [column, setColumn] = useState(initialStep?.column || '');
    const [targetType, setTargetType] = useState<'string' | 'number' | 'date'>(initialStep?.targetType || 'number');
    const [removeInvalid, setRemoveInvalid] = useState(initialStep?.removeInvalid ?? false);

    const columnOptions = columns.map(c => ({ value: c.name, label: `${c.name} (${c.type})` }));
    const isValid = column !== '';

    const handleSave = () => {
        onSave({
            type: 'type-casting',
            column,
            targetType,
            removeInvalid
        });
    };

    return (
        <ModalWrapper title="Type Casting" icon={RefreshCw} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <SelectField
                    label="Column"
                    value={column}
                    onChange={setColumn}
                    options={columnOptions}
                    placeholder="Select column"
                />

                <SelectField
                    label="Convert to"
                    value={targetType}
                    onChange={v => setTargetType(v as 'string' | 'number' | 'date')}
                    options={[
                        { value: 'number', label: 'Number' },
                        { value: 'string', label: 'Text' },
                        { value: 'date', label: 'Date' }
                    ]}
                />

                <CheckboxField
                    label="Remove rows with invalid values"
                    checked={removeInvalid}
                    onChange={setRemoveInvalid}
                    description="If unchecked, invalid values will become null"
                />
            </div>
        </ModalWrapper>
    );
};

interface NormalizationModalProps {
    columns: ColumnSchema[];
    initialStep?: NormalizationStep;
    onSave: (step: NormalizationStep) => void;
    onClose: () => void;
}

export const NormalizationModal: React.FC<NormalizationModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [selectedColumns, setSelectedColumns] = useState<string[]>(initialStep?.columns || []);
    const [method, setMethod] = useState<NormalizationMethod>(initialStep?.method || 'min-max');
    const [createNewColumn, setCreateNewColumn] = useState(initialStep?.createNewColumn ?? true);
    const [suffix, setSuffix] = useState(initialStep?.newColumnSuffix || '_normalized');

    const numericColumns = columns.filter(c => c.type === 'number').map(c => c.name);
    const isValid = selectedColumns.length > 0;

    const handleSave = () => {
        onSave({
            type: 'normalization',
            columns: selectedColumns,
            method,
            createNewColumn,
            newColumnSuffix: createNewColumn ? suffix : undefined
        });
    };

    return (
        <ModalWrapper title="Normalization" icon={TrendingUp} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <MultiSelectField
                    label="Columns"
                    selected={selectedColumns}
                    onChange={setSelectedColumns}
                    options={numericColumns}
                />

                <SelectField
                    label="Method"
                    value={method}
                    onChange={v => setMethod(v as NormalizationMethod)}
                    options={[
                        { value: 'min-max', label: 'Min-Max Scaling (0-1)' },
                        { value: 'z-score', label: 'Z-Score Standardization' },
                        { value: 'log', label: 'Log Transform' },
                        { value: 'abs', label: 'Absolute Value' },
                        { value: 'pct-change', label: 'Percentage Change (row-based)' }
                    ]}
                />

                <CheckboxField
                    label="Create new column"
                    checked={createNewColumn}
                    onChange={setCreateNewColumn}
                    description="If unchecked, the original column will be overwritten"
                />

                {createNewColumn && (
                    <TextField
                        label="New column suffix"
                        value={suffix}
                        onChange={setSuffix}
                        placeholder="_normalized"
                    />
                )}
            </div>
        </ModalWrapper>
    );
};

interface CalculatedColumnModalProps {
    columns: ColumnSchema[];
    initialStep?: CalculatedColumnStep;
    onSave: (step: CalculatedColumnStep) => void;
    onClose: () => void;
}

export const CalculatedColumnModal: React.FC<CalculatedColumnModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [columnName, setColumnName] = useState(initialStep?.newColumnName || '');
    const [formula, setFormula] = useState(initialStep?.formula || '');

    const columnNames = columns.map(c => c.name);
    const validation = useMemo(
        () => formula ? validateFormula(formula, columnNames) : { valid: false, error: 'Enter a formula' },
        [formula, columnNames]
    );
    const isValid = columnName !== '' && validation.valid;

    const handleSave = () => {
        onSave({
            type: 'calculated-column',
            newColumnName: columnName,
            formula
        });
    };

    return (
        <ModalWrapper title="Calculated Column" icon={Calculator} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <TextField
                    label="New column name"
                    value={columnName}
                    onChange={setColumnName}
                    placeholder="e.g., revenue_per_user"
                />

                <TextField
                    label="Formula"
                    value={formula}
                    onChange={setFormula}
                    placeholder="e.g., revenue / users"
                    error={!validation.valid && formula ? validation.error : undefined}
                />

                {/* Column Reference Helper */}
                <div className="space-y-2">
                    <label className="block text-sm text-gray-400">Available columns</label>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-white/5 rounded-lg">
                        {columnNames.map(col => {
                            // Wrap column names with special characters in brackets
                            const needsBrackets = /[^a-zA-Z0-9_]/.test(col);
                            const colRef = needsBrackets ? `[${col}]` : col;
                            return (
                                <button
                                    key={col}
                                    onClick={() => setFormula(f => {
                                        // If formula ends with open paren, insert column and close it
                                        if (f.endsWith('(')) {
                                            return f + colRef + ')';
                                        }
                                        // Otherwise add with space if needed
                                        return f + (f && !f.endsWith(' ') && !f.endsWith('(') ? ' ' : '') + colRef;
                                    })}
                                    className="px-2 py-0.5 text-xs bg-white/5 text-gray-300 rounded hover:bg-neon-gold/10 hover:text-neon-gold transition-colors"
                                >
                                    {col}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Functions Helper */}
                <div className="space-y-2">
                    <label className="block text-sm text-gray-400">Functions</label>
                    <div className="flex flex-wrap gap-1.5 p-2 bg-white/5 rounded-lg">
                        {ALLOWED_FUNCTIONS.map(fn => (
                            <button
                                key={fn}
                                onClick={() => setFormula(f => f + (f && !f.endsWith(' ') && !f.endsWith('(') ? ' ' : '') + fn + '(')}
                                className="px-2 py-0.5 text-xs bg-purple-500/10 text-purple-300 rounded hover:bg-purple-500/20 transition-colors"
                            >
                                {fn}()
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500">
                        Click a function, then click a column to insert it inside the parentheses.
                    </p>
                </div>
            </div>
        </ModalWrapper>
    );
};

interface ColumnManagementModalProps {
    columns: ColumnSchema[];
    initialStep?: ColumnManagementStep;
    onSave: (step: ColumnManagementStep) => void;
    onClose: () => void;
}

export const ColumnManagementModal: React.FC<ColumnManagementModalProps> = ({
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    const [column, setColumn] = useState(initialStep?.column || '');
    const [action, setAction] = useState<ColumnManagementAction>(initialStep?.action || 'rename');
    const [newName, setNewName] = useState(initialStep?.newName || '');
    const [newPosition, setNewPosition] = useState(String(initialStep?.newPosition ?? 0));

    const columnOptions = columns.map(c => ({ value: c.name, label: c.name }));
    const isValid = column !== '' && (action !== 'rename' || newName !== '');

    const handleSave = () => {
        onSave({
            type: 'column-management',
            action,
            column,
            newName: action === 'rename' ? newName : undefined,
            hidden: action === 'hide' ? true : undefined,
            newPosition: action === 'reorder' ? parseInt(newPosition) : undefined
        });
    };

    return (
        <ModalWrapper title="Column Management" icon={Columns} onClose={onClose} onSave={handleSave} isValid={isValid}>
            <div className="space-y-4">
                <SelectField
                    label="Column"
                    value={column}
                    onChange={setColumn}
                    options={columnOptions}
                    placeholder="Select column"
                />

                <SelectField
                    label="Action"
                    value={action}
                    onChange={v => setAction(v as ColumnManagementAction)}
                    options={[
                        { value: 'rename', label: 'Rename column' },
                        { value: 'hide', label: 'Hide from visualization' },
                        { value: 'delete', label: 'Delete column' },
                        { value: 'reorder', label: 'Change position' }
                    ]}
                />

                {action === 'rename' && (
                    <TextField
                        label="New name"
                        value={newName}
                        onChange={setNewName}
                        placeholder="Enter new column name"
                    />
                )}

                {action === 'reorder' && (
                    <TextField
                        label="New position (0-based index)"
                        value={newPosition}
                        onChange={setNewPosition}
                        placeholder="0"
                        helpText={`Current columns: ${columns.length}. Enter 0 for first position.`}
                    />
                )}

                {action === 'delete' && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-sm text-red-400">
                            ⚠️ This will permanently remove the column from the transformed dataset.
                        </p>
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

// ============================================================================
// Modal Factory
// ============================================================================

interface DataPrepModalProps {
    stepType: DataPrepStepType;
    columns: ColumnSchema[];
    initialStep?: DataPrepStep;
    onSave: (step: DataPrepStep) => void;
    onClose: () => void;
}

export const DataPrepModal: React.FC<DataPrepModalProps> = ({
    stepType,
    columns,
    initialStep,
    onSave,
    onClose
}) => {
    switch (stepType) {
        case 'null-handling':
            return <NullHandlingModal columns={columns} initialStep={initialStep as NullHandlingStep} onSave={onSave} onClose={onClose} />;
        case 'duplicate-handling':
            return <DuplicateHandlingModal columns={columns} initialStep={initialStep as DuplicateHandlingStep} onSave={onSave} onClose={onClose} />;
        case 'string-cleaning':
            return <StringCleaningModal columns={columns} initialStep={initialStep as StringCleaningStep} onSave={onSave} onClose={onClose} />;
        case 'type-casting':
            return <TypeCastingModal columns={columns} initialStep={initialStep as DataTypeCastingStep} onSave={onSave} onClose={onClose} />;
        case 'normalization':
            return <NormalizationModal columns={columns} initialStep={initialStep as NormalizationStep} onSave={onSave} onClose={onClose} />;
        case 'calculated-column':
            return <CalculatedColumnModal columns={columns} initialStep={initialStep as CalculatedColumnStep} onSave={onSave} onClose={onClose} />;
        case 'column-management':
            return <ColumnManagementModal columns={columns} initialStep={initialStep as ColumnManagementStep} onSave={onSave} onClose={onClose} />;
        default:
            return null;
    }
};

export default DataPrepModal;
